import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Home,
  Inventory,
  ShoppingCart,
  Assignment,
  Receipt,
  Person,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../config/constants';

interface CustomerSidebarProps {
  onItemClick?: () => void;
}

const CustomerSidebar: React.FC<CustomerSidebarProps> = ({ onItemClick }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: t('navigation.dashboard'), icon: <Home />, path: ROUTES.DASHBOARD },
    { text: t('navigation.products'), icon: <Inventory />, path: ROUTES.PRODUCTS },
    { text: t('navigation.cart'), icon: <ShoppingCart />, path: ROUTES.CART },
    { text: t('navigation.orders'), icon: <Assignment />, path: ROUTES.ORDERS },
    { text: t('navigation.invoices'), icon: <Receipt />, path: ROUTES.INVOICES },
    { text: t('navigation.profile'), icon: <Person />, path: ROUTES.PROFILE },
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
    onItemClick?.();
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <img
          src="/Mowilex-Corporate.png"
          alt="Mowilex Logo"
          style={{ height: 32 }}
        />
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'error.main', width: 36, height: 36, fontSize: '0.9rem' }}>
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {user?.full_name || user?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1 }}>
        <List>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  selected={isActive}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                      '& .MuiListItemIcon-root': { color: 'white' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? 'white' : 'text.secondary', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Logout */}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary={t('navigation.logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

export default CustomerSidebar;
